/**
 * navPaletteStore — the "jump to any form" source for the Ctrl/⌘+K command
 * palette (CLAUDE.md standing rule 14, §18.6, ADR-0013 Phase 3, the mouse-free
 * launcher).
 *
 * The palette's primary source is the live hotkey registry (`useHotkeyList`) —
 * on-screen actions + the `g`-prefix nav chords.  That only reaches the handful
 * of chord destinations, so on its own Ctrl+K can't take you to an arbitrary one
 * of the ~150 forms.  AppShell knows the whole nav tree AND the current user's
 * permissions (`canSee`) and the navigate action (`open`), so it computes the set
 * of forms THIS user can open and publishes them here; the palette merges them in.
 *
 * Kept OUT of the hotkey registry on purpose: these are palette-only navigation
 * entries — they must never appear in the `?` cheat sheet (which would bloat it
 * with 150 rows) and must not add 150 no-op bindings the global keydown
 * dispatcher walks on every keystroke.  Mirrors the hotkeyStore snapshot pattern.
 */
import { create } from 'zustand';

/** One "jump to a form" palette entry (published by AppShell, read by the palette). */
export interface NavPaletteItem {
  /** The page id — also the React key in the palette list. */
  id: string;
  /** Display label (already resolved via navLabel, so admin renames apply). */
  label: string;
  /** Group label shown as the palette row's context chip (e.g. "Settings"). */
  group: string;
  /** Navigate to this form (AppShell's permission-safe `open(id)`). */
  run: () => void;
}

/** Reactive snapshot the palette subscribes to. Empty until AppShell publishes
 *  (e.g. on the login page, where there is no in-app navigation yet). */
export const useNavPalette = create<{ items: NavPaletteItem[] }>(() => ({ items: [] }));

/** Replace the whole nav-palette list (AppShell calls this once permissions load,
 *  and clears it with `[]` on unmount). */
export function setNavPaletteItems(items: NavPaletteItem[]): void {
  useNavPalette.setState({ items });
}
